import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
  type SortingState,
  type ColumnDef,
} from '@tanstack/react-table';
import { ArrowUpDown, ArrowUp, ArrowDown, Filter, Download, Plus, Inbox, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { fetchTickets, fetchTicketsExport, fetchOperatorsForFilter, fetchInternalUsers, fetchMentionableUsers, fetchGroupsForFilter, fetchSavedFilters, bulkUpdateTicketsApi } from '@/hooks/useTickets';
import type { TicketListItem, OperatorListItem, InternalUserItem, GroupFilterItem, MentionableUser, BulkUpdateRequest } from '@uniticket/shared';
import { BulkStatusDialog } from '@/components/tickets/BulkStatusDialog';
import { BulkAssignDialog } from '@/components/tickets/BulkAssignDialog';
import { BulkPriorityDialog } from '@/components/tickets/BulkPriorityDialog';
import { StatusBadge } from '@/components/custom/StatusBadge';
import { PriorityBadge } from '@/components/custom/PriorityBadge';
import { TypeBadge } from '@/components/custom/TypeBadge';
import { MultiSelectFilter } from '@/components/custom/MultiSelectFilter';
import { SingleSelectFilter } from '@/components/custom/SingleSelectFilter';
import { SlaStatusFilter } from '@/components/custom/SlaStatusFilter';
import { DateRangeFilter } from '@/components/custom/DateRangeFilter';
import { SavedFiltersDropdown } from '@/components/custom/SavedFiltersDropdown';
import { useTicketFilters } from '@/hooks/useTicketFilters';
import { STATUS_OPTIONS, PRIORITY_OPTIONS } from '@/constants/filter-options';
import {
  categoryOptionsForType, categoryValuesForType, categoryLabel,
  addressSourceOptions, addressActionOptions, ticketCategoryLabel,
} from '@/constants/category-options';
import { exportTicketsCsv } from '@/lib/csv-export';
import { ScrollSyncTable } from '@/components/custom/ScrollSyncTable';

// Type keys for tab filtering (labels resolved via i18n)
const TYPE_KEYS = ['ALL', 'OPEN_ORDER', 'ADDRESS_MANAGEMENT', 'SUPPORT_REQUEST', 'BUILD'] as const;

// Columns that support server-side sorting
const SORTABLE_COLUMNS = new Set(['ticketNumber', 'subject', 'status', 'priority', 'updatedAt', 'createdAt', 'ticketType']);

export function TicketList() {
  const { t } = useTranslation(['tickets', 'common']);
  const { isInternal, isExternal, user } = useAuth();
  const navigate = useNavigate();
  const { filters, setFilter, setFilters, clearAll, toApiParams, toFilterConfig, applyFilterConfig, activeFilterCount } = useTicketFilters();

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [tickets, setTickets] = useState<TicketListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [exporting, setExporting] = useState(false);

  // Bulk action state
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkPriorityOpen, setBulkPriorityOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Debounced search: local state for immediate UI, synced to URL params
  const [searchInput, setSearchInput] = useState(filters.q);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    // Sync search input from URL on external navigation (e.g. back button)
    setSearchInput(filters.q);
  }, [filters.q]);

  function handleSearchChange(value: string) {
    setSearchInput(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setFilter('q', value || null);
    }, 300);
  }

  // Operator and user lists for filter dropdowns (internal only)
  const [operators, setOperators] = useState<OperatorListItem[]>([]);
  const [internalUsers, setInternalUsers] = useState<InternalUserItem[]>([]);
  const [dropdownsLoading, setDropdownsLoading] = useState(false);

  // Creator + group lists for filter dropdowns (all roles, operator-isolated server-side)
  const [creators, setCreators] = useState<MentionableUser[]>([]);
  const [groups, setGroups] = useState<GroupFilterItem[]>([]);

  useEffect(() => {
    if (!isInternal) return;
    setDropdownsLoading(true);
    Promise.all([
      fetchOperatorsForFilter().catch(() => ({ data: [] })),
      fetchInternalUsers().catch(() => ({ data: [] })),
    ]).then(([opRes, userRes]) => {
      setOperators(opRes.data);
      setInternalUsers(userRes.data);
    }).finally(() => setDropdownsLoading(false));
  }, [isInternal]);

  useEffect(() => {
    Promise.all([
      fetchMentionableUsers().catch(() => ({ data: [] as MentionableUser[] })),
      fetchGroupsForFilter().catch(() => ({ data: [] as GroupFilterItem[] })),
    ]).then(([uRes, gRes]) => {
      setCreators(uRes.data);
      setGroups(gRes.data);
    });
  }, []);

  // Default saved filter auto-load (only on mount with no URL params)
  const defaultFilterLoaded = useRef(false);
  useEffect(() => {
    if (defaultFilterLoaded.current) return;
    defaultFilterLoaded.current = true;
    // Only auto-load if URL has no filter params
    const hasParams = window.location.search.length > 1;
    if (hasParams) return;
    fetchSavedFilters()
      .then((res) => {
        const defaultFilter = res.data.find((f) => f.isDefault);
        if (defaultFilter) {
          applyFilterConfig(defaultFilter.filterConfig as Record<string, unknown>);
        }
      })
      .catch(() => {});
  }, [applyFilterConfig]);

  // Server-side sorting state derived from URL params
  const sorting: SortingState = useMemo(() => {
    if (!filters.sort) return [];
    return [{ id: filters.sort, desc: filters.order === 'desc' }];
  }, [filters.sort, filters.order]);

  function handleSortingChange(updater: SortingState | ((prev: SortingState) => SortingState)) {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length === 0) {
      setFilters({ sort: null, order: null });
    } else {
      setFilters({ sort: newSorting[0].id, order: newSorting[0].desc ? 'desc' : 'asc' });
    }
  }

  // Active type filter derived from URL
  const activeType = filters.ticketType.length === 1 ? filters.ticketType[0] : 'ALL';

  // Filter type tabs to only show types the user can read
  const canRead = user?.effectivePermissions?.canRead ?? [];
  const canCreate = user?.effectivePermissions?.canCreate ?? [];
  const visibleTypeKeys = TYPE_KEYS.filter((k) => k === 'ALL' || canRead.includes(k));

  function handleTypeChange(type: string) {
    // Classification filters are scoped to the ticket type — OPEN_ORDER's QUOTE lives in
    // order_subcategory, SUPPORT_REQUEST's QUESTION in support_category. A value left over from
    // the previous type matches nothing and would silently empty the list, so clear all three in
    // the SAME update (two setFilter calls would be two history entries).
    setFilters({
      ticketType: type === 'ALL' ? null : type,
      category: null,
      addrSource: null,
      addrAction: null,
    });
  }

  const categoryOptions = useMemo(() => categoryOptionsForType(activeType, t), [activeType, t]);

  // One source of truth for the disabled state: categoryOptionsForType() returns [] for exactly
  // the two types with no category list of their own — 'ALL' and ADDRESS_MANAGEMENT — so the pill
  // needs no special-case list. Only the WORDING differs between them. The pill is never hidden.
  const categoryDisabled = categoryOptions.length === 0;
  const categoryHint = activeType === 'ADDRESS_MANAGEMENT'
    ? t('tickets:list.filters.categoryAddressHint')
    : t('tickets:list.filters.categoryDisabledHint');

  // Defensive prune for URLs the UI did not produce (bookmarked, shared, hand-edited, or a saved
  // filter written before a value was retired). Validated against what is VALID for the type,
  // NOT against what the pill offers — a hand-written `category=MOVE_FTU` is meaningful and must
  // survive; only values belonging to a different type are dropped. Terminates in one pass.
  useEffect(() => {
    if (activeType === 'ALL' || filters.category.length === 0) return;
    const allowed = categoryValuesForType(activeType);
    const valid = filters.category.filter((c: string) => allowed.includes(c));
    if (valid.length !== filters.category.length) {
      setFilter('category', valid.length > 0 ? valid : null, { replace: true });
    }
  }, [activeType, filters.category, setFilter]);

  // Load tickets from API
  const loadTickets = useCallback(async () => {
    try {
      setLoading(true);
      const params = toApiParams();
      const res = await fetchTickets(params);
      setTickets(res.data);
      setTotal(res.meta?.total ?? res.data.length);
    } catch {
      // API errors handled by apiFetch
    } finally {
      setLoading(false);
    }
  }, [toApiParams]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  // Build active filter chips for display
  // Group filter options: append the operator name only when a group name is duplicated across
  // operators (e.g. two "Default" groups), so internal users can tell them apart.
  const groupOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const g of groups) counts.set(g.name, (counts.get(g.name) ?? 0) + 1);
    return groups.map((g) => ({
      value: g.id,
      label: (counts.get(g.name) ?? 0) > 1 && g.operatorName ? `${g.name} (${g.operatorName})` : g.name,
    }));
  }, [groups]);

  const activeFilterChips = useMemo(() => {
    const chips: { key: string; label: string; removeValue?: string }[] = [];

    for (const s of filters.status) {
      chips.push({ key: 'status', label: `${t('tickets:list.filters.status')} ${t(`common:status.${s}`, s)}`, removeValue: s });
    }
    for (const p of filters.priority) {
      chips.push({ key: 'priority', label: `${t('tickets:list.filters.priority')} ${t(`common:priority.${p}`, p)}`, removeValue: p });
    }
    // One chip per selected classification value, mirroring status/priority. categoryLabel
    // translates by key convention rather than by looking the value up in the offered options,
    // so a retired value (MOVE_FTU) from a hand-written URL still reads "Move FTU" here.
    for (const c of filters.category) {
      chips.push({ key: 'category', label: `${t('tickets:list.filters.category')} ${categoryLabel(c, activeType, t)}`, removeValue: c });
    }
    for (const s of filters.addrSource) {
      chips.push({ key: 'addrSource', label: `${t('tickets:list.filters.addrSource')} ${t(`common:addressSource.${s}`, s)}`, removeValue: s });
    }
    for (const a of filters.addrAction) {
      chips.push({ key: 'addrAction', label: `${t('tickets:list.filters.addrAction')} ${t(`common:addressAction.${a}`, a)}`, removeValue: a });
    }
    if (filters.operatorId) {
      const op = operators.find((o) => o.id === filters.operatorId);
      chips.push({ key: 'operatorId', label: `${t('tickets:list.filters.operator')} ${op?.name || 'Selected'}` });
    }
    if (filters.assignedTo) {
      const user = internalUsers.find((u) => u.id === filters.assignedTo);
      chips.push({ key: 'assignedTo', label: `${t('tickets:list.filters.assigned')} ${user?.displayName || 'Selected'}` });
    }
    if (filters.mentionedUserId) {
      const mu = internalUsers.find((u) => u.id === filters.mentionedUserId);
      chips.push({ key: 'mentionedUserId', label: `${t('tickets:list.filters.mentioned')} ${mu?.displayName || 'Selected'}` });
    }
    if (filters.createdBy) {
      const cr = creators.find((u) => u.id === filters.createdBy);
      chips.push({ key: 'createdBy', label: `${t('tickets:list.filters.creator')} ${cr?.displayName || 'Selected'}` });
    }
    if (filters.groupId) {
      const g = groupOptions.find((o) => o.value === filters.groupId);
      chips.push({ key: 'groupId', label: `${t('tickets:list.filters.group')} ${g?.label || 'Selected'}` });
    }
    if (filters.slaStatus) {
      chips.push({ key: 'slaStatus', label: `${t('tickets:list.filters.sla')} ${t(`common:sla.${filters.slaStatus}`, filters.slaStatus)}` });
    }
    if (filters.createdAtFrom || filters.createdAtTo) {
      chips.push({ key: 'dateRange', label: t('tickets:list.filters.dateRange') });
    }
    if (filters.escalated) {
      chips.push({ key: 'escalated', label: t('tickets:list.filters.escalated') });
    }
    return chips;
  }, [filters, operators, internalUsers, creators, groups, groupOptions, activeType, t]);

  function removeChip(chip: { key: string; removeValue?: string }) {
    if (chip.key === 'status' && chip.removeValue) {
      const remaining = filters.status.filter((s) => s !== chip.removeValue);
      setFilter('status', remaining.length > 0 ? remaining : null);
    } else if (chip.key === 'priority' && chip.removeValue) {
      const remaining = filters.priority.filter((p) => p !== chip.removeValue);
      setFilter('priority', remaining.length > 0 ? remaining : null);
    } else if (chip.key === 'category' && chip.removeValue) {
      const remaining = filters.category.filter((c) => c !== chip.removeValue);
      setFilter('category', remaining.length > 0 ? remaining : null);
    } else if (chip.key === 'addrSource' && chip.removeValue) {
      const remaining = filters.addrSource.filter((s) => s !== chip.removeValue);
      setFilter('addrSource', remaining.length > 0 ? remaining : null);
    } else if (chip.key === 'addrAction' && chip.removeValue) {
      const remaining = filters.addrAction.filter((a) => a !== chip.removeValue);
      setFilter('addrAction', remaining.length > 0 ? remaining : null);
    } else if (chip.key === 'dateRange') {
      setFilter('createdAtFrom', null);
      setFilter('createdAtTo', null);
    } else {
      setFilter(chip.key, null);
    }
  }

  // Column definitions
  const columns: ColumnDef<TicketListItem>[] = useMemo(() => [
    ...(!isExternal
      ? [
          {
            id: 'select',
            enableSorting: false,
            header: ({ table }: { table: ReturnType<typeof useReactTable<TicketListItem>> }) => (
              <input
                type="checkbox"
                checked={table.getIsAllRowsSelected()}
                onChange={table.getToggleAllRowsSelectedHandler()}
                className="rounded border-white/40"
              />
            ),
            cell: ({ row }: { row: { getIsSelected: () => boolean; getToggleSelectedHandler: () => (e: unknown) => void } }) => (
              <input
                type="checkbox"
                checked={row.getIsSelected()}
                onChange={row.getToggleSelectedHandler()}
                className="rounded border-slate-300"
                onClick={(e) => e.stopPropagation()}
              />
            ),
            size: 40,
          } satisfies ColumnDef<TicketListItem>,
        ]
      : []),
    {
      accessorKey: 'ticketNumber',
      header: t('tickets:list.cols.ticketNumber'),
      cell: ({ row }) => (
        <span className="font-mono font-semibold text-brand-rose text-xs">{row.original.ticketNumber}</span>
      ),
    },
    {
      accessorKey: 'ticketType',
      header: t('tickets:list.cols.type'),
      cell: ({ row }) => <TypeBadge type={row.original.ticketType} />,
    },
    {
      // Display-only: there is no server sort field for "category" (it is four different
      // columns), so sorting is disabled rather than silently sorting by something else.
      id: 'category',
      header: t('tickets:list.cols.category'),
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-xs text-slate-600 whitespace-nowrap">{ticketCategoryLabel(row.original, t)}</span>
      ),
    },
    {
      accessorKey: 'subject',
      header: t('tickets:list.cols.subject'),
      cell: ({ row }) => (
        <Link
          to={`/tickets/${row.original.id}`}
          className="font-medium text-slate-800 hover:text-brand-rose transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          {row.original.subject}
        </Link>
      ),
      size: 250,
    },
    ...(isInternal
      ? [
          {
            id: 'operatorName',
            header: t('tickets:list.cols.operator'),
            enableSorting: false,
            cell: ({ row }: { row: { original: TicketListItem } }) => (
              <span className="text-slate-600">{row.original.operator.name}</span>
            ),
          } satisfies ColumnDef<TicketListItem>,
        ]
      : []),
    {
      accessorKey: 'status',
      header: t('tickets:list.cols.status'),
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'priority',
      header: t('tickets:list.cols.priority'),
      cell: ({ row }) => <PriorityBadge priority={row.original.priority} />,
    },
    {
      id: 'assignee',
      header: t('tickets:list.cols.assignedTo'),
      enableSorting: false,
      cell: ({ row }) => {
        const assignee = row.original.assignee;
        const name = assignee?.displayName;
        if (!name) return <span className="text-xs text-slate-400">{t('common:fields.unassigned')}</span>;
        const initials = name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
        // The masked placeholder (e.g. "Unifiber team", id '') is shown in full; real names
        // abbreviate to "First L." to save width.
        const shortName = assignee?.id === '' ? name : `${name.split(' ')[0]} ${name.split(' ').pop()?.[0]}.`;
        return (
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600">
              {initials}
            </div>
            <span className="text-slate-600 text-xs whitespace-nowrap">{shortName}</span>
          </div>
        );
      },
    },
    {
      id: 'creatorName',
      header: t('tickets:list.cols.creator'),
      enableSorting: false,
      cell: ({ row }) => {
        const name = row.original.createdBy?.displayName;
        return name ? (
          <span className="text-slate-600 text-xs whitespace-nowrap">{name}</span>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        );
      },
    },
    {
      id: 'creatorGroup',
      header: t('tickets:list.cols.group'),
      enableSorting: false,
      cell: ({ row }) => {
        const names = row.original.creatorGroupNames ?? [];
        return names.length > 0 ? (
          <span className="text-slate-600 text-xs">{names.join(', ')}</span>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: t('tickets:list.cols.created'),
      cell: ({ row }) => {
        const date = new Date(row.original.createdAt);
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);
        const rel = days > 0 ? t('common:time.daysAgo', { count: days }) : hours > 0 ? t('common:time.hoursAgo', { count: hours }) : t('common:time.justNow');
        const abs = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        return (
          <div className="flex flex-col leading-tight whitespace-nowrap" title={date.toLocaleString('en-GB')}>
            <span className="text-xs text-slate-600">{abs}</span>
            <span className="text-[11px] text-slate-400">{rel}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'updatedAt',
      header: t('tickets:list.cols.updated'),
      cell: ({ row }) => {
        const date = new Date(row.original.updatedAt);
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);
        const label = days > 0 ? t('common:time.daysAgo', { count: days }) : hours > 0 ? t('common:time.hoursAgo', { count: hours }) : t('common:time.justNow');
        return <span className="text-xs text-slate-400" title={date.toLocaleString('en-GB')}>{label}</span>;
      },
    },
  ], [isExternal, isInternal, t]);

  const table = useReactTable({
    data: tickets,
    columns,
    state: { sorting, rowSelection },
    onSortingChange: handleSortingChange,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualSorting: true,
    enableRowSelection: !isExternal,
  });

  const selectedCount = Object.keys(rowSelection).filter((k) => rowSelection[k]).length;
  const hasAnyFilter = activeFilterCount > 0 || filters.ticketType.length > 0 || filters.q;

  // Horizontal-scroll affordances for the wide table:
  // - frozenLeft: row-identity columns pinned to the left while scrolling (left-offset cascade;
  //   the 40px offset for ticketNumber accounts for the 40px select checkbox column when present).
  // - HIDE_BELOW_XL: lower-priority columns hidden until the xl breakpoint, so the table fits
  //   common laptop widths and the scroll chrome auto-hides.
  const frozenLeft: Record<string, number> = isExternal
    ? { ticketNumber: 0 }
    : { select: 0, ticketNumber: 40 };
  const HIDE_BELOW_XL = new Set(['creatorGroup', 'createdAt', 'updatedAt']);

  async function handleExportCsv() {
    if (total === 0) {
      toast.info(t('tickets:list.noExport'));
      return;
    }
    setExporting(true);
    try {
      // Fetch ALL rows matching the current filters (not just the loaded page).
      const res = await fetchTicketsExport(toApiParams());
      const rows = res.data;
      if (rows.length === 0) {
        toast.info(t('tickets:list.noExport'));
        return;
      }
      exportTicketsCsv(rows, !!isInternal);
      toast.success(t('tickets:list.exported', { count: rows.length }));
    } catch {
      // apiFetch surfaces the error toast
    } finally {
      setExporting(false);
    }
  }

  function getSelectedTicketIds(): string[] {
    return Object.keys(rowSelection)
      .filter((k) => rowSelection[k])
      .map((idx) => tickets[Number(idx)]?.id)
      .filter(Boolean);
  }

  async function handleBulkUpdate(update: BulkUpdateRequest['update']) {
    const ticketIds = getSelectedTicketIds();
    if (ticketIds.length === 0) return;

    setBulkLoading(true);
    try {
      const result = await bulkUpdateTicketsApi({ ticketIds, update });
      const { succeeded, failed, total: count } = result.summary;

      if (failed === 0) {
        toast.success(t('tickets:list.bulkUpdated', { count: succeeded }));
      } else if (succeeded === 0) {
        toast.error(t('tickets:list.bulkFailed', { count: failed }));
      } else {
        toast.warning(t('tickets:list.bulkPartial', { success: succeeded, total: count, failed }));
      }

      setRowSelection({});
      loadTickets();
    } catch {
      toast.error(t('tickets:list.bulkError'));
    } finally {
      setBulkLoading(false);
    }
  }

  function handleClearAll() {
    clearAll();
    setSearchInput('');
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="bg-white border border-slate-200 rounded-xl mb-6 overflow-hidden">
        {/* Top toolbar row */}
        <div className="px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <SavedFiltersDropdown
              onLoad={applyFilterConfig}
              onGetCurrentConfig={toFilterConfig}
            />
            <div className="relative">
              <Filter className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={t('tickets:list.filterPlaceholder')}
                className="w-72 pl-10 pr-4 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 transition-all placeholder:text-slate-400"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportCsv}
              disabled={exporting}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              {exporting ? t('tickets:list.exporting') : t('tickets:list.exportCsv')}
            </button>
            {canCreate.length > 0 && (
              <Link
                to="/tickets/new"
                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5 bg-gradient-to-r from-brand-rose to-brand-orange"
              >
                <Plus className="w-4 h-4" />
                {t('tickets:list.createTicket')}
              </Link>
            )}
          </div>
        </div>

        {/* Filter chips row */}
        <div className="px-6 pb-4 flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider mr-1">{t('tickets:list.filters.type')}</span>
          {visibleTypeKeys.map((key) => (
            <button
              key={key}
              onClick={() => handleTypeChange(key)}
              className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                activeType === key
                  ? 'bg-gradient-to-r from-brand-rose to-brand-orange text-white border-transparent'
                  : 'border-slate-200 text-slate-600 bg-white hover:bg-slate-50'
              }`}
            >
              {key === 'ALL' ? t('common:ticketType.ALL') : t(`common:ticketTypeShort.${key}`)}
            </button>
          ))}

          <div className="w-px h-5 bg-slate-200 mx-2" />

          {/*
            Category sits next to the type pills that scope it. ALWAYS rendered — greyed when it
            cannot apply ('ALL', and ADDRESS_MANAGEMENT which has its own two pills below), never
            removed, so the bar keeps the same controls in the same places across every type.
          */}
          <MultiSelectFilter
            label={t('tickets:list.cols.category')}
            options={categoryOptions}
            selected={filters.category}
            onSelectionChange={(values) => setFilter('category', values)}
            disabled={categoryDisabled}
            disabledHint={categoryHint}
          />
          {activeType === 'ADDRESS_MANAGEMENT' && (
            <>
              <MultiSelectFilter
                label={t('tickets:addressWizard.source')}
                options={addressSourceOptions(t)}
                selected={filters.addrSource}
                onSelectionChange={(values) => setFilter('addrSource', values)}
              />
              <MultiSelectFilter
                label={t('tickets:addressWizard.action')}
                options={addressActionOptions(t)}
                selected={filters.addrAction}
                onSelectionChange={(values) => setFilter('addrAction', values)}
              />
            </>
          )}

          <MultiSelectFilter
            label={t('tickets:list.cols.status')}
            options={STATUS_OPTIONS}
            selected={filters.status}
            onSelectionChange={(values) => setFilter('status', values)}
            renderOption={(opt) => <StatusBadge status={opt.value} />}
          />
          <MultiSelectFilter
            label={t('tickets:list.cols.priority')}
            options={PRIORITY_OPTIONS}
            selected={filters.priority}
            onSelectionChange={(values) => setFilter('priority', values)}
            renderOption={(opt) => <PriorityBadge priority={opt.value} />}
          />

          {isInternal && (
            <>
              <SingleSelectFilter
                label={t('tickets:list.cols.operator')}
                options={operators.map((o) => ({ value: o.id, label: o.name }))}
                selected={filters.operatorId}
                onSelectionChange={(value) => setFilter('operatorId', value)}
                loading={dropdownsLoading}
                searchPlaceholder={t('tickets:list.searchOperators')}
              />
              <SingleSelectFilter
                label={t('tickets:list.cols.assignedTo')}
                options={internalUsers.map((u) => ({ value: u.id, label: u.displayName }))}
                selected={filters.assignedTo}
                onSelectionChange={(value) => setFilter('assignedTo', value)}
                loading={dropdownsLoading}
                searchPlaceholder={t('tickets:list.searchUsers')}
              />
              <SingleSelectFilter
                label={t('tickets:list.cols.mentionedUser')}
                options={internalUsers.map((u) => ({ value: u.id, label: u.displayName }))}
                selected={filters.mentionedUserId}
                onSelectionChange={(value) => setFilter('mentionedUserId', value)}
                loading={dropdownsLoading}
                searchPlaceholder={t('tickets:list.searchUsers')}
              />
              {user && (
                <button
                  onClick={() =>
                    setFilter('mentionedUserId', filters.mentionedUserId === user.id ? null : user.id)
                  }
                  className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                    filters.mentionedUserId === user.id
                      ? 'border-brand-rose/40 bg-brand-rose/5 text-brand-rose'
                      : 'border-slate-200 text-slate-600 bg-white hover:bg-slate-50'
                  }`}
                >
                  {t('tickets:list.mentionsMe')}
                </button>
              )}
            </>
          )}

          <SingleSelectFilter
            label={t('tickets:list.cols.creator')}
            options={creators.map((u) => ({ value: u.id, label: u.displayName }))}
            selected={filters.createdBy}
            onSelectionChange={(value) => setFilter('createdBy', value)}
            searchPlaceholder={t('tickets:list.searchCreators')}
          />
          <SingleSelectFilter
            label={t('tickets:list.cols.group')}
            options={groupOptions}
            selected={filters.groupId}
            onSelectionChange={(value) => setFilter('groupId', value)}
            searchPlaceholder={t('tickets:list.searchGroups')}
          />

          <button
            onClick={() => setFilter('escalated', filters.escalated ? null : 'true')}
            className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
              filters.escalated
                ? 'border-brand-rose/40 bg-brand-rose/5 text-brand-rose'
                : 'border-slate-200 text-slate-600 bg-white hover:bg-slate-50'
            }`}
          >
            {t('tickets:list.filters.escalated')}
          </button>

          <SlaStatusFilter
            selected={filters.slaStatus}
            onSelectionChange={(value) => setFilter('slaStatus', value)}
          />

          <DateRangeFilter
            from={filters.createdAtFrom}
            to={filters.createdAtTo}
            onChange={(from, to) => {
              setFilter('createdAtFrom', from);
              setFilter('createdAtTo', to);
            }}
          />

          <div className="w-px h-5 bg-slate-200 mx-2" />
          <button
            onClick={handleClearAll}
            className="text-xs font-medium text-slate-400 hover:text-brand-rose transition-colors"
          >
            {t('common:actions.clearAll')}
          </button>
        </div>

        {/* Active filter chips */}
        {activeFilterChips.length > 0 && (
          <div className="px-6 pb-4 flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider mr-1">{t('tickets:list.filters.active')}</span>
            {activeFilterChips.map((chip, i) => (
              <span
                key={`${chip.key}-${chip.removeValue || i}`}
                className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full bg-brand-rose/10 text-brand-rose border border-brand-rose/20"
              >
                {chip.label}
                <button onClick={() => removeChip(chip)} className="hover:text-red-600 transition-colors">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Bulk action bar */}
      {selectedCount > 0 && (
        <div className="mb-4 bg-gradient-to-r from-brand-purple/5 via-brand-rose/5 to-brand-orange/5 border border-brand-rose/20 rounded-xl px-6 py-2.5">
          <div className="flex items-center gap-4">
            <span className="text-sm font-semibold text-brand-rose">{t('tickets:list.selected', { count: selectedCount })}</span>
            <div className="flex items-center gap-2">
              {bulkLoading && <Loader2 className="w-4 h-4 animate-spin text-brand-rose" />}
              <button onClick={() => setBulkStatusOpen(true)} disabled={bulkLoading} className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-md hover:bg-slate-50 transition-colors disabled:opacity-50">
                {t('tickets:list.changeStatus')}
              </button>
              <button onClick={() => setBulkAssignOpen(true)} disabled={bulkLoading} className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-md hover:bg-slate-50 transition-colors disabled:opacity-50">
                {t('tickets:list.assignTo')}
              </button>
              <button onClick={() => setBulkPriorityOpen(true)} disabled={bulkLoading} className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-md hover:bg-slate-50 transition-colors disabled:opacity-50">
                {t('tickets:list.changePriority')}
              </button>
            </div>
            <button
              onClick={() => setRowSelection({})}
              className="ml-auto text-xs font-medium text-slate-400 hover:text-slate-600 transition-colors"
            >
              {t('tickets:list.deselectAll')}
            </button>
          </div>
        </div>
      )}

      {/* Results count + per page */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">
          {loading ? t('tickets:list.loading') : t('tickets:list.showingOf', { from: tickets.length, to: tickets.length, total })}
        </p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">{t('tickets:list.perPage')}</span>
          <select
            value={table.getState().pagination.pageSize}
            onChange={(e) => table.setPageSize(Number(e.target.value))}
            className="text-sm bg-white border border-slate-200 rounded-md px-2 py-1 text-slate-600 focus:outline-none focus:ring-2 focus:ring-brand-rose/20"
          >
            {[10, 25, 50, 100].map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <ScrollSyncTable>
        <table className="w-full text-sm">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="gradient-band text-white text-left">
                  {headerGroup.headers.map((header) => {
                    const isSortable = SORTABLE_COLUMNS.has(header.column.id);
                    const sortDir = header.column.getIsSorted();
                    const fLeft = frozenLeft[header.column.id];
                    const isFrozen = fLeft !== undefined;
                    const hideCls = HIDE_BELOW_XL.has(header.column.id) ? 'hidden xl:table-cell' : '';
                    return (
                      <th
                        key={header.id}
                        className={`px-4 py-3 font-semibold text-xs uppercase tracking-wider whitespace-nowrap ${hideCls} ${isFrozen ? 'sticky z-20 bg-brand-purple' : ''} ${header.column.id === 'ticketNumber' ? 'border-r border-white/20' : ''}`}
                        style={{
                          ...(header.column.getSize() !== 150 ? { minWidth: header.column.getSize() } : {}),
                          ...(isFrozen ? { left: fLeft } : {}),
                        }}
                      >
                        {header.isPlaceholder ? null : isSortable ? (
                          <button
                            onClick={header.column.getToggleSortingHandler()}
                            className="flex items-center gap-1 hover:text-white/80 transition-colors"
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {sortDir === 'asc' ? (
                              <ArrowUp className="w-3 h-3" />
                            ) : sortDir === 'desc' ? (
                              <ArrowDown className="w-3 h-3" />
                            ) : (
                              <ArrowUpDown className="w-3 h-3 opacity-40" />
                            )}
                          </button>
                        ) : (
                          flexRender(header.column.columnDef.header, header.getContext())
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={table.getAllColumns().length} className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-8 h-8 border-2 border-brand-rose/30 border-t-brand-rose rounded-full animate-spin" />
                      <p className="text-sm text-slate-500">{t('tickets:list.loading')}</p>
                    </div>
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={table.getAllColumns().length} className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                        <Inbox className="w-6 h-6 text-slate-400" />
                      </div>
                      {hasAnyFilter ? (
                        <>
                          <p className="text-sm font-medium text-slate-600">{t('tickets:list.noMatch')}</p>
                          <button
                            onClick={handleClearAll}
                            className="text-xs font-medium text-brand-rose hover:text-brand-purple transition-colors"
                          >
                            {t('tickets:list.clearAllFilters')}
                          </button>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-slate-600">{t('tickets:list.noTickets')}</p>
                          <Link to="/tickets/new" className="text-xs font-medium text-brand-rose hover:text-brand-purple transition-colors">
                            {t('tickets:list.createFirst')}
                          </Link>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className={`group/row hover:bg-slate-50 transition-colors cursor-pointer ${
                      row.getIsSelected() ? 'bg-brand-rose/[0.03]' : ''
                    }`}
                    onClick={() => navigate(`/tickets/${row.original.id}`)}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const fLeft = frozenLeft[cell.column.id];
                      const isFrozen = fLeft !== undefined;
                      const hideCls = HIDE_BELOW_XL.has(cell.column.id) ? 'hidden xl:table-cell' : '';
                      return (
                        <td
                          key={cell.id}
                          className={`px-4 py-3 ${hideCls} ${isFrozen ? `sticky z-10 bg-white group-hover/row:bg-slate-50 ${cell.column.id === 'ticketNumber' ? 'shadow-[6px_0_8px_-6px_rgba(0,0,0,0.15)]' : ''}` : ''}`}
                          style={isFrozen ? { left: fLeft } : undefined}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
        </table>
      </ScrollSyncTable>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <p className="text-sm text-slate-500">
          {t('tickets:list.pageOf', { page: table.getState().pagination.pageIndex + 1, totalPages: table.getPageCount() })}
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="px-3 py-1.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-md hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('common:actions.previous')}
          </button>
          <button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="px-3 py-1.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-md hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('common:actions.next')}
          </button>
        </div>
      </div>

      {/* Bulk action dialogs */}
      <BulkStatusDialog
        open={bulkStatusOpen}
        onOpenChange={setBulkStatusOpen}
        selectedCount={selectedCount}
        onConfirm={(status) => handleBulkUpdate({ status })}
      />
      <BulkAssignDialog
        open={bulkAssignOpen}
        onOpenChange={setBulkAssignOpen}
        selectedCount={selectedCount}
        internalUsers={internalUsers}
        onConfirm={(userId) => handleBulkUpdate({ assignedTo: userId })}
      />
      <BulkPriorityDialog
        open={bulkPriorityOpen}
        onOpenChange={setBulkPriorityOpen}
        selectedCount={selectedCount}
        onConfirm={(priority) => handleBulkUpdate({ priority })}
      />
    </div>
  );
}
